import React from 'react'
import { DEVELOPMENT } from '../consts'
import { Restaurant } from './restaurant'

// Die vor Ort unterschriebenen Dokumente hakt der Root selbst ab. Die veröffentlichte Speisekarte und
// das verbundene Stripe-Konto ergeben sich aus dem Stand.
export type OnboardingStep = {
	id: string
	title: string
	// Zeigt das Formular, das die angehakten Dokumente als PDF zum Ausdrucken liefert; ohne Haken.
	contract?: boolean
	companyForm?: boolean
	stripe?: boolean
	// Zählt nicht für den Abschluss des Onboardings, etwa Stripe: ohne Konto zahlen Kunden an der Tür.
	optional?: boolean
	// Erledigt, sobald eine Menü-Version aktiv ist. Import, Prüfung und Freigabe laufen in der
	// Menüverwaltung.
	activeMenu?: boolean
	// Zeigt die Firmen-E-Mail als gesperrtes Feld — der Wert, der hier eingetragen werden muss.
	showCompanyEmail?: boolean
}

// Schritte, deren Haken sich aus einem Stand ergibt statt von Hand gesetzt zu werden.
export const isAutomaticStep = (step: OnboardingStep): boolean =>
	step.activeMenu === true || step.stripe === true

// Der Guide zeigt je Abschnitt eine Karte; die Nummerierung läuft über alle Abschnitte durch. Die
// Schritte zum Abhaken stehen auch in api/src/utils/onboardingLog.ts: Die Freigabe verlangt sie alle.
export const ONBOARDING_SECTIONS: {
	title: string
	// Zeigt neben der Überschrift die aktive Zahlungsart der Firma.
	showPaymentMethod?: boolean
	steps: OnboardingStep[]
}[] = [
	{
		title: 'Vertrag und Speisekarte',
		steps: [
			{
				id: 'COMPANY_DETAILS_SET',
				title: 'Firmen- und Restaurantdaten kontrollieren',
				companyForm: true,
			},
			{
				id: 'PARTNER_DOCUMENTS',
				title: 'Dokumente ausdrucken',
				contract: true,
				optional: true,
			},
			{
				id: 'PARTNER_CONTRACT_SIGNED_RESTAURANT',
				title: 'Dienstleistungsvertrag vom Restaurant unterschrieben',
			},
			{
				id: 'MENU_CONFIRMATION_SIGNED_RESTAURANT',
				title: 'Menübestätigung vom Restaurant unterschrieben',
			},
			{
				id: 'PARTNER_CONTRACT_SIGNED_BEYER',
				title: 'Dienstleistungsvertrag von Beyer Development unterschrieben',
			},
			{
				id: 'DEVICE_CONTRACT_SIGNED_RESTAURANT',
				title: 'Empfangsbestätigung Endgerät vom Restaurant unterschrieben (optional)',
				optional: true,
			},
		],
	},
	{
		title: 'Zahlung',
		showPaymentMethod: true,
		steps: [
			{
				id: 'STRIPE_ONBOARDING',
				title: 'Stripe-Onboarding (optional)',
				stripe: true,
				optional: true,
			},
		],
	},
	{
		title: 'Unterlagen',
		steps: [
			{
				id: 'TRADE_INFORMATION_OBTAINED',
				title: 'Gewerbeauskunft eingeholt',
			},
			{
				id: 'INSURANCE_PHOTO',
				title: 'Nachweis über eine Betriebshaftpflichtversicherung einholen',
			},
		],
	},
	{
		title: 'Zugang',
		steps: [
			{
				id: 'ADMIN_USER_CREATED',
				title: 'Restaurant-Admin anlegen',
				showCompanyEmail: true,
			},
			{
				id: 'DASHBOARD_ACCESS',
				title: 'Dashboard-Zugang zeigen',
			},
			{
				id: 'SUNMI_SETUP',
				title: 'Sunmi-Gerät einrichten',
			},
		],
	},
	{
		title: 'Menü',
		steps: [
			{
				id: 'MENU_ACTIVE',
				title: 'Non-Preview Speisekarte veröffentlicht',
				activeMenu: true,
			},
		],
	},
]

export const ONBOARDING_STEPS: OnboardingStep[] = ONBOARDING_SECTIONS.flatMap(
	(section) => section.steps
)

// Der reduzierte Guide des Preview-Tabs: Die Karte geht ohne Vertrag, Zahlung, Unterlagen und Prüfung
// online. Die Firmendaten braucht sie trotzdem, sie stehen im Impressum.
export const PREVIEW_ONBOARDING_SECTIONS: typeof ONBOARDING_SECTIONS = [
	{
		title: 'Preview',
		steps: [
			{
				id: 'COMPANY_DETAILS_SET',
				title: 'Firmen- und Restaurantdaten kontrollieren',
				companyForm: true,
			},
			{
				id: 'MENU_ACTIVE',
				title: 'Preview Speisekarte veröffentlicht',
				activeMenu: true,
			},
		],
	},
]

// Wie weit das Onboarding eines Restaurants ist — für den Guide des Roots wie für den Admin, der bis
// zum Abschluss nur einen Hinweis sieht. Der Preview-Tab will eine aktive Preview, das Onboarding eine
// aktive Version ohne Preview.
export const useOnboardingState = (restaurant?: Restaurant | null, preview = false) =>
	React.useMemo(() => {
		const checkedSteps = new Set((restaurant?.onboardingLog ?? []).map((entry) => entry.step))
		const isStepDone = (step: OnboardingStep): boolean =>
			step.activeMenu
				? (restaurant?.menuHistory ?? []).some(
						(item) => item.state === 'ACTIVE' && item.preview === preview
				  )
				: // Erledigt, sobald Stripe die aktive Zahlungsart ist; in Produktion zählt nur das Live-Konto,
				// eine Test-Zahlungsart reicht nur in der Entwicklung.
				step.stripe
				? restaurant?.paymentMethod === 'STRIPE' ||
				  (DEVELOPMENT && restaurant?.paymentMethod === 'STRIPE_TEST')
				: checkedSteps.has(step.id)
		return {
			checkedSteps,
			isStepDone,
			completed: ONBOARDING_STEPS.every((step) => step.optional || isStepDone(step)),
			loading: restaurant === undefined,
		}
	}, [restaurant, preview])
